import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import SEO from '../components/SEO';
import TrendingHero from '../components/trending/TrendingHero';
import RegionSelector from '../components/trending/RegionSelector';
import TrendingGrid from '../components/trending/TrendingGrid';
import TrendingGridSkeleton from '../components/trending/TrendingGridSkeleton';
import detectRegion from '../utils/detectRegion';
import {
  REGION_LABEL_LONG,
  normaliseRegion,
  trendingPath,
  showPath,
} from '../utils/regions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const REDIRECT_URL = window.location.origin + '/dashboard';
const AUTH_URL = `${process.env.REACT_APP_AUTH_URL}/?redirect=${encodeURIComponent(REDIRECT_URL)}`;

const Trending = ({ user }) => {
  const navigate = useNavigate();
  const { region: regionParam } = useParams();
  const isLoggedIn = !!user;

  const initialRegion =
    normaliseRegion(regionParam) || normaliseRegion(user?.region) || 'global';

  const [region, setRegion] = useState(initialRegion);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  // Keep region in sync with URL param
  useEffect(() => {
    const fromUrl = normaliseRegion(regionParam);
    if (fromUrl && fromUrl !== region) {
      setRegion(fromUrl);
    }
  }, [regionParam, region]);

  const loadTrending = useCallback(async (r) => {
    setLoading(true);
    setError(null);
    try {
      const resp = await axios.get(`${API}/public/trending`, {
        params: { region: r, limit: 24 },
      });
      setData(resp.data);
    } catch (e) {
      setError(
        (e.response && e.response.data && e.response.data.detail) ||
          'Could not load trending shows. Please try again in a moment.'
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTrending(region);
  }, [region, loadTrending]);

  const handleRegionChange = (next) => {
    setRegion(next);
    // Update URL without reloading
    navigate(trendingPath(next), { replace: true });
    // Scroll grid into view on mobile pill tap
    if (typeof window !== 'undefined' && window.innerWidth < 720) {
      window.scrollTo({ top: 160, behavior: 'smooth' });
    }
  };

  const handleSignIn = () => {
    window.location.href = AUTH_URL;
  };

  // Clicking a trending card navigates to the public show detail page,
  // preserving the current region as a query param (omit for 'global').
  const handleOpenShow = (show) => {
    if (!show || !show.tmdb_id) return;
    navigate(showPath(show.tmdb_id, region));
  };

  const regionLabel = REGION_LABEL_LONG[region] || 'the world';
  const pageTitle = `Trending TV in ${regionLabel} this week — baad.tv`;
  const pageDescription = `See the most popular TV shows trending in ${regionLabel} right now. Updated weekly. Track shows, rate them, and share with your flock on baad.tv.`;
  const canonical = trendingPath(region);
  // Use the #1 trending show's backdrop as the per-region OG image so
  // social previews of /trending/NZ feel distinct from /trending/AU etc.
  const topShow = data && data.shows && data.shows[0];
  const pageImage = (topShow && (topShow.backdrop_url || topShow.image_url)) || undefined;

  // Imperatively set <title> (react-helmet-async title application has been
  // unreliable on this route; setting document.title in an effect is the
  // pattern used by FAQPage and is deterministic).
  useEffect(() => {
    const previous = document.title;
    document.title = pageTitle;
    return () => {
      document.title = previous;
    };
  }, [pageTitle]);

  // Inject JSON-LD ItemList into <head> directly so search engines / scrapers
  // can pick it up without relying on the helmet provider mounting in time.
  useEffect(() => {
    if (!data || !data.shows || data.shows.length === 0) return undefined;
    const jsonLd = {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: `Trending TV in ${regionLabel} (${new Date().toISOString().slice(0, 10)})`,
      itemListOrder: 'https://schema.org/ItemListOrderDescending',
      numberOfItems: data.shows.length,
      itemListElement: data.shows.map((s, idx) => ({
        '@type': 'ListItem',
        position: idx + 1,
        item: {
          '@type': 'TVSeries',
          name: s.title,
          url: s.tmdb_url,
          image: s.image_url,
          datePublished: s.first_air_date || undefined,
          aggregateRating: s.vote_average
            ? {
                '@type': 'AggregateRating',
                ratingValue: s.vote_average,
                ratingCount: s.vote_count || 1,
                bestRating: 10,
                worstRating: 0,
              }
            : undefined,
        },
      })),
    };
    const tag = document.createElement('script');
    tag.type = 'application/ld+json';
    tag.setAttribute('data-trending-jsonld', region);
    tag.text = JSON.stringify(jsonLd);
    document.head.appendChild(tag);
    return () => {
      try { document.head.removeChild(tag); } catch (e) { /* noop */ }
    };
  }, [data, region, regionLabel]);

  return (
    <>
      <SEO
        title={pageTitle}
        description={pageDescription}
        url={canonical}
        image={pageImage}
      />

      <div className="trending-page">
        {/* Mini public header */}
        <nav className="public-nav" data-testid="public-nav">
          <a
            className="brand"
            href="/"
            data-testid="public-nav-brand"
            onClick={(e) => {
              e.preventDefault();
              navigate('/');
            }}
          >
            <img
              src="/sheep-logo.png"
              alt="baad.tv logo"
              width="32"
              height="32"
            />
            <span>baad.tv</span>
          </a>
          <div className="nav-actions">
            {isLoggedIn ? (
              <button
                type="button"
                data-testid="public-nav-dashboard"
                onClick={() => navigate('/dashboard')}
                className="nav-btn primary"
              >
                Go to dashboard
              </button>
            ) : (
              <button
                type="button"
                data-testid="public-nav-signin"
                onClick={handleSignIn}
                className="nav-btn primary"
              >
                Join the flock
              </button>
            )}
          </div>
        </nav>

        <TrendingHero
          region={region}
          isLoggedIn={isLoggedIn}
          onSignIn={handleSignIn}
        />

        <RegionSelector active={region} onChange={handleRegionChange} />

        {loading && <TrendingGridSkeleton />}

        {error && !loading && (
          <div className="error" data-testid="trending-error">
            <p>{error}</p>
            <button
              type="button"
              className="retry-btn"
              onClick={() => loadTrending(region)}
              data-testid="trending-retry"
            >
              Try again
            </button>
          </div>
        )}

        {!loading && !error && data && (
          <TrendingGrid
            shows={data.shows}
            onOpen={handleOpenShow}
          />
        )}

        <footer className="trending-footer">
          <div className="footer-inner">
            <p>Data courtesy of TMDB. Updated hourly.</p>
            <div className="footer-links">
              <button onClick={() => navigate('/')} data-testid="trending-footer-home">Home</button>
              <span>•</span>
              <button onClick={() => navigate('/faq')} data-testid="trending-footer-faq">FAQ</button>
              <span>•</span>
              {isLoggedIn ? (
                <button onClick={() => navigate('/dashboard')} data-testid="trending-footer-dashboard">Dashboard</button>
              ) : (
                <button onClick={handleSignIn} data-testid="trending-footer-signin">Join the flock</button>
              )}
            </div>
          </div>
        </footer>

        <style jsx>{`
          .trending-page {
            background: #0a0a0a;
            min-height: 100vh;
            color: #fff;
            font-family: 'Manrope', system-ui, -apple-system, BlinkMacSystemFont, sans-serif;
          }
          .public-nav {
            position: sticky;
            top: 0;
            z-index: 50;
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 0.85rem 1.5rem;
            background: rgba(10, 10, 10, 0.7);
            backdrop-filter: blur(18px);
            -webkit-backdrop-filter: blur(18px);
            border-bottom: 1px solid #1a1a1a;
          }
          .brand {
            display: inline-flex;
            align-items: center;
            gap: 0.55rem;
            text-decoration: none;
            color: #E62F88;
            font-weight: 800;
            font-size: 1.25rem;
            letter-spacing: -0.01em;
          }
          .brand img {
            display: block;
          }
          .nav-actions {
            display: flex;
            gap: 0.5rem;
          }
          .nav-btn {
            border: 1px solid #2a2a2a;
            background: #161616;
            color: #fff;
            padding: 0.55rem 1rem;
            font-weight: 700;
            font-size: 0.85rem;
            border-radius: 8px;
            cursor: pointer;
            transition: background 0.15s, border-color 0.15s, transform 0.15s;
          }
          .nav-btn:hover {
            background: #1f1f1f;
          }
          .nav-btn.primary {
            background: #E62F88;
            color: #000;
            border-color: #E62F88;
          }
          .nav-btn.primary:hover {
            background: #ff4f9e;
            transform: translateY(-1px);
          }
          .loading {
            text-align: center;
            padding: 4rem 1.5rem;
            color: #a1a1aa;
            font-size: 1rem;
          }
          .error {
            max-width: 520px;
            margin: 3rem auto;
            text-align: center;
            padding: 2rem 1.5rem;
            background: #131313;
            border: 1px solid #3a1f2b;
            border-radius: 14px;
            color: #fca5a5;
          }
          .retry-btn {
            margin-top: 1rem;
            background: #E62F88;
            color: #000;
            border: none;
            padding: 0.6rem 1.2rem;
            border-radius: 8px;
            font-weight: 700;
            cursor: pointer;
          }
          .trending-footer {
            margin-top: 6rem;
            border-top: 1px solid #1a1a1a;
            padding: 2.5rem 1.5rem;
          }
          .footer-inner {
            max-width: 1280px;
            margin: 0 auto;
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 1rem;
            flex-wrap: wrap;
            color: #6f6f78;
            font-size: 0.85rem;
          }
          .footer-links {
            display: flex;
            align-items: center;
            gap: 0.65rem;
          }
          .footer-links button {
            background: none;
            border: none;
            color: #E62F88;
            cursor: pointer;
            font-size: 0.85rem;
            text-decoration: underline;
            padding: 0;
          }
          .footer-links span {
            color: #333;
          }
        `}</style>
      </div>
    </>
  );
};

export default Trending;
